import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../config/api';
import PageNavbar from './PageNavbar';
import { formatDateDisplay } from '../utils/formatDate';
import './ExpensesReview.css';

function formatInr(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
}

export default function OrderApprovals() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState(null);
  const [busyId, setBusyId] = useState(null);
  const [rejectNotes, setRejectNotes] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    setMsg(null);
    try {
      const { data } = await api.get('/orders', {
        params: { approvalStatus: 'awaiting', startDate: '2000-01-01', endDate: '2100-12-31' }
      });
      setOrders(data.orders || []);
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Could not load approvals' });
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id) => {
    setBusyId(id);
    try {
      await api.post(`/orders/${id}/approve`);
      setMsg({ type: 'ok', text: 'Order approved — now in confirmed orders' });
      load();
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Approve failed' });
    } finally {
      setBusyId(null);
    }
  };

  const reject = async (id) => {
    const notes = String(rejectNotes[id] || '').trim();
    if (!notes) {
      setMsg({ type: 'error', text: 'Enter a rejection note first' });
      return;
    }
    setBusyId(id);
    try {
      await api.post(`/orders/${id}/reject-approval`, { notes });
      setMsg({ type: 'ok', text: 'Order rejected and cancelled' });
      load();
    } catch (error) {
      setMsg({ type: 'error', text: error.response?.data?.error || 'Reject failed' });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="expenses-review">
      <PageNavbar />
      <div className="container">
        <div className="expenses-review__header">
          <div>
            <h1>Order approvals</h1>
            <p>
              Online Orders under ₹500 wait here until Admin approves. Approved orders appear in the normal
              confirmed orders list.
            </p>
          </div>
          <div className="expenses-review__summary">
            <div>
              <em>Awaiting</em>
              <strong>{orders.length}</strong>
            </div>
          </div>
        </div>

        {msg ? (
          <div className={`expenses-review__msg expenses-review__msg--${msg.type}`}>{msg.text}</div>
        ) : null}

        <div className="expenses-review__table-wrap">
          {loading ? (
            <div className="expenses-review__empty">Loading…</div>
          ) : orders.length === 0 ? (
            <div className="expenses-review__empty">No orders awaiting approval</div>
          ) : (
            <table className="expenses-review__table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Delivery</th>
                  <th>Customer</th>
                  <th>Total</th>
                  <th>Created by</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o) => (
                  <tr key={o.id}>
                    <td>
                      <Link to={`/orders/${o.id}`}>
                        <strong>{o.order_number}</strong>
                      </Link>
                      <div style={{ color: '#78716c', fontSize: '0.8rem' }}>{o.category}</div>
                    </td>
                    <td>
                      {formatDateDisplay(o.delivery_date)}
                      {o.delivery_time ? ` · ${String(o.delivery_time).slice(0, 5)}` : ''}
                    </td>
                    <td>
                      {o.customer_name}
                      {o.customer_phone ? (
                        <div style={{ color: '#78716c', fontSize: '0.8rem' }}>{o.customer_phone}</div>
                      ) : null}
                    </td>
                    <td>{formatInr(o.total_amount)}</td>
                    <td>{o.created_by_name || '—'}</td>
                    <td>
                      <div className="expenses-review__actions">
                        <button
                          type="button"
                          className="btn btn-sm btn-primary"
                          disabled={busyId === o.id}
                          onClick={() => approve(o.id)}
                        >
                          Approve
                        </button>
                        <input
                          type="text"
                          placeholder="Reject reason"
                          value={rejectNotes[o.id] || ''}
                          onChange={(e) =>
                            setRejectNotes((prev) => ({ ...prev, [o.id]: e.target.value }))
                          }
                          style={{ minWidth: 140, padding: '6px 8px', borderRadius: 6, border: '1px solid #cfc6b8' }}
                        />
                        <button
                          type="button"
                          className="btn btn-sm btn-danger"
                          disabled={busyId === o.id}
                          onClick={() => reject(o.id)}
                        >
                          Reject
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
